import { Button } from '@hqo/ui-atoms';
import { useDateTimeFormatters } from 'shared/hooks';
import { X } from 'lucide-react';
import { useEffect } from 'react';
import { useIntl } from 'react-intl';

interface CancelVisitConfirmationProps {
  onCancel: VoidFunction;
  onConfirm: VoidFunction;
  visitorName: string;
  arrivalDate: Date;
  departureDate?: Date;
  isRecurring?: boolean;
  scheduleText?: string | null;
}

export const CancelVisitConfirmation = ({
  onCancel,
  onConfirm,
  visitorName,
  arrivalDate,
  departureDate,
  isRecurring = false,
  scheduleText,
}: CancelVisitConfirmationProps): JSX.Element => {
  const { formatNumericDate, formatTime } = useDateTimeFormatters();
  const { formatMessage } = useIntl();

  const formattedDate = formatNumericDate(arrivalDate);
  const arrivalTime = formatTime(arrivalDate);
  const departureTime = departureDate ? formatTime(departureDate) : null;

  // Prevent body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-white/50 backdrop-blur-sm" onClick={onCancel} aria-hidden="true" />

      {/* Modal content */}
      <div className="relative bg-white rounded-2xl shadow-xl border border-gray-200 max-w-md w-full max-h-[90vh] overflow-y-auto">
        {/* Close button */}
        <button
          type="button"
          onClick={onCancel}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
          aria-label={formatMessage({ id: 'buttons.cancel' })}
        >
          <X className="h-5 w-5" />
        </button>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Title */}
          <div className="pr-8">
            <h2 className="text-xl font-semibold text-gray-900">
              {formatMessage({
                id: isRecurring ? 'visitsListV2.cancelVisit.titleRecurring' : 'visitsListV2.cancelVisit.title',
              })}
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              {formatMessage({
                id: isRecurring
                  ? 'visitsListV2.cancelVisit.descriptionRecurring'
                  : 'visitsListV2.cancelVisit.description',
              })}
            </p>
          </div>

          {/* Visit details */}
          <div className="space-y-3">
            <div className="text-sm">
              <span className="font-medium text-gray-900">
                {formatMessage({ id: 'visitsListV2.cancelVisit.visitor' })}:{' '}
              </span>
              <span className="text-gray-700">{visitorName}</span>
            </div>

            <div className="text-sm">
              <span className="font-medium text-gray-900">
                {formatMessage({ id: 'visitsListV2.cancelVisit.date' })}:{' '}
              </span>
              <span className="text-gray-700">{formattedDate}</span>
            </div>

            <div className="text-sm">
              <span className="font-medium text-gray-900">
                {formatMessage({ id: 'visitsListV2.cancelVisit.time' })}:{' '}
              </span>
              <span className="text-gray-700">
                {arrivalTime}
                {departureTime && ` - ${departureTime}`}
              </span>
            </div>

            {scheduleText && (
              <div className="text-sm">
                <span className="font-medium text-gray-900">
                  {formatMessage({ id: 'visitsListV2.cancelVisit.schedule' })}:{' '}
                </span>
                <span className="text-gray-700">{scheduleText}</span>
              </div>
            )}
          </div>

          {/* Action buttons */}
          <div className="flex gap-3">
            <Button
              onClick={onCancel}
              variant="outline"
              className="flex-1"
              data-cy="keep-visit"
              data-testid="keep-visit"
            >
              {formatMessage({ id: 'visitsListV2.cancelVisit.keepVisit' })}
            </Button>
            <Button
              onClick={onConfirm}
              variant="destructive"
              className="flex-1"
              data-cy="confirm-cancel-visit"
              data-testid="confirm-cancel-visit"
            >
              {formatMessage({
                id: isRecurring ? 'visitsListV2.cancelVisit.cancelVisits' : 'visitsListV2.cancelVisit.cancelVisit',
              })}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
